import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SearchInput } from "@/components/common/SearchInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dateFmt } from "@/lib/format";
import { CalendarClock, Plus, Lock } from "lucide-react";
import { toast } from "sonner";

const FREQUENCIES = ["monthly", "quarterly", "annual"];

export default function PerformanceCycles() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { me } = useApp();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", period: "", frequency: "quarterly", start_date: "", end_date: "" });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [frequencyFilter, setFrequencyFilter] = useState("all");
  const { data: cycles, isLoading } = useQuery({ queryKey: ["perf-cycles"], queryFn: () => get("/hrms/performance/cycles") });
  const filtered = useMemo(() => {
    return (cycles || []).filter((c) => {
      if (search && !`${c.name} ${c.period}`.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (frequencyFilter !== "all" && c.frequency !== frequencyFilter) return false;
      return true;
    });
  }, [cycles, search, statusFilter, frequencyFilter]);

  const create = useMutation({
    mutationFn: (body) => post("/hrms/performance/cycles", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["perf-cycles"] }); toast.success("Cycle created"); setAdding(false); setForm({ name: "", period: "", frequency: "quarterly", start_date: "", end_date: "" }); },
  });
  const close = useMutation({
    mutationFn: (cid) => post(`/hrms/performance/cycles/${cid}/close`, { _actor: me?.id }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["perf-cycles"] });
      if (res.winners?.length) toast.success(`Cycle closed — ${res.winners.map((w) => w.name).join(", ")} named Employee of the Month`);
      else toast.success("Cycle closed");
    },
  });

  return (
    <div>
      <PageHeader title="Performance Cycles" subtitle="Create review cycles and close them once reviews are in.">
        <Button data-testid="add-cycle-btn" onClick={() => setAdding((s) => !s)}><Plus className="mr-1.5 h-4 w-4" />New Cycle</Button>
      </PageHeader>

      {isLoading ? <Skeleton className="h-64" /> : (
        <Section>
          {adding && (
            <div className="border-b border-border/60 bg-muted/30 p-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input data-testid="cycle-name" className="mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
                <div><Label>Period Label</Label><Input data-testid="cycle-period" className="mt-1" placeholder="e.g. Jul – Dec 2026" value={form.period} onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))} /></div>
                <div>
                  <Label>Frequency</Label>
                  <Select value={form.frequency} onValueChange={(v) => setForm((f) => ({ ...f, frequency: v }))}>
                    <SelectTrigger className="mt-1" data-testid="cycle-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent>{FREQUENCIES.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div />
                <div><Label>Start Date</Label><Input data-testid="cycle-start" type="date" className="mt-1" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} /></div>
                <div><Label>End Date</Label><Input data-testid="cycle-end" type="date" className="mt-1" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} /></div>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
                <Button size="sm" data-testid="cycle-submit" onClick={() => create.mutate({ ...form, status: "active" })}>Create</Button>
              </div>
            </div>
          )}
          {!cycles?.length ? <EmptyState icon={CalendarClock} title="No cycles yet" /> : (
            <>
              <div className="flex items-center gap-3 border-b border-border/60 p-4">
                <SearchInput testid="cycle-search" placeholder="Search name or period" value={search} onChange={setSearch} />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40" data-testid="cycle-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={frequencyFilter} onValueChange={setFrequencyFilter}>
                  <SelectTrigger className="w-40" data-testid="cycle-frequency-filter"><SelectValue placeholder="All frequencies" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All frequencies</SelectItem>
                    {FREQUENCIES.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {filtered.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">No cycles match your search or filters.</div>
              ) : (
            <div className="divide-y divide-border">
              {filtered.map((c) => (
                <div key={c.id} className="flex items-center justify-between px-4 py-3 text-sm">
                  <div>
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.period} · {c.frequency} · {dateFmt(c.start_date)} – {dateFmt(c.end_date)} · {c.review_count} review(s)</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={c.status} />
                    <Button size="sm" variant="outline" onClick={() => navigate("/hrms/performance")}>View Summary</Button>
                    {c.status !== "closed" ? (
                      <Button size="sm" variant="destructive" data-testid={`close-cycle-${c.id}`} onClick={() => close.mutate(c.id)}>Close Cycle</Button>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground"><Lock className="h-3 w-3" />Closed</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
              )}
            </>
          )}
        </Section>
      )}
    </div>
  );
}
